import { iconForCategory } from '../lib/categoryGrid';
import { displayPieceName } from '../lib/format';
import {
  type OrderLine,
  buildOrderConfirmation,
  orderTotalUnits,
  removeLine,
  stepLineQty,
} from '../lib/orderReview';
import { Icon } from './Icon';
import { NoImageFill } from './NoImageFill';
import { OnStandBadge } from './OnStandBadge';

/**
 * S6 Order Review scene (R10 — showcase-conversation-synergy.md §5, client-scenarios.md
 * Scenario 1 beats 9–10). The load-bearing scene.
 *
 * Before place_order / send_spec_sheet the showcase renders "your selection": product images,
 * names, tap-adjustable quantities, remove ✕. Gigi asks "shall I send this?"; the visitor taps
 * Confirm → the kiosk emits `order-confirmed {items:[{sku, qty}]}` with EXACTLY what is shown.
 * This ground-truths the order by explicit visual confirmation (fixes audit finding #1).
 *
 * Voice sets quantities ("three of the brass planters"), touch corrects (steppers). R12: on Confirm
 * the scene advances to S7 'close' (the container drives `ui.scene {close}` locally + the server
 * echoes it) — the close card (recap + keyboard-first email) lives in CloseScene now, not inline
 * here. This keeps a single close surface and removes the duplicate post-confirm form.
 *
 * Design system: tokens only, Icon primitive, .reveal motion, day+night aware.
 */

type Props = {
  /** The order lines (from selection + last-shown cards; seeded qty from a voice scene payload). */
  lines: OrderLine[];
  onLinesChange: (lines: OrderLine[]) => void;
  /** Confirm → emit order-confirmed with exactly the displayed items (parent advances to 'close'). */
  onConfirm: (items: Array<{ sku: string; qty: number }>) => void;
  /**
   * R16 V1 — the live review mirror (contract §14). Called with the NEXT lines on every stepper /
   * removal change so the server's per-session mirror tracks the screen. The seed emit + the final
   * pre-confirm emit are driven by the parent container (ShowcaseScene). Optional (pure-render tests
   * omit it).
   */
  onReviewUpdated?: (lines: OrderLine[]) => void;
  /** Back to browsing (touch always allowed to leave the scene). */
  onBack: () => void;
};

export function OrderReview({ lines, onLinesChange, onConfirm, onReviewUpdated, onBack }: Props) {
  // R16 V1 — apply a line change AND mirror it. Every stepper/removal flows through here so the
  // server's per-session mirror can never lag the screen (dev-77 M1: a lost qty-2 stepper edit).
  function applyChange(next: OrderLine[]) {
    onLinesChange(next);
    onReviewUpdated?.(next);
  }

  function handleConfirm() {
    // R16 V1 — a FINAL mirror emit precedes the confirm so the server commits from the exact screen
    // state even if a trailing change's mirror was still debounced upstream.
    onReviewUpdated?.(lines);
    onConfirm(buildOrderConfirmation(lines).items);
  }

  return (
    <div className="reveal flex flex-col gap-5" data-testid="order-review">
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="font-display text-xl text-ink">Your selection</h3>
        {/* R28 (7.3) — the piece/unit counter promoted to a soft brass badge (it's load-bearing:
            Gigi confirms exactly this before place_order). */}
        {lines.length > 0 && (
          <span
            data-testid="order-count-badge"
            className="inline-flex items-center gap-1.5 rounded-full bg-brass-soft px-3 py-1 text-xs text-brass ring-1 ring-brass/30"
          >
            <Icon name="package" size={13} />
            {lines.length === 1 ? '1 piece' : `${lines.length} pieces`} &middot;{' '}
            {orderTotalUnits(lines)} units
          </span>
        )}
      </div>

      {lines.length === 0 ? (
        <div className="flex flex-col items-center gap-3 py-10 text-center">
          <Icon name="package" size={24} className="text-faint" />
          <p className="text-sm text-subtle">
            Nothing selected yet — add a few pieces and I'll gather them here.
          </p>
          <button
            type="button"
            onClick={onBack}
            className="btn-ghost px-5 py-2.5 text-sm rounded-full"
          >
            Keep browsing
          </button>
        </div>
      ) : (
        <>
          <ul className="flex flex-col gap-3">
            {lines.map((line) => (
              <li
                key={line.sku}
                data-testid={`order-line-${line.sku}`}
                className="flex items-center gap-3 rounded-lg bg-surface ring-1 ring-line p-3 shadow-elev-rest"
              >
                <div className="h-16 w-16 shrink-0 overflow-hidden rounded-md bg-canvas/40 grid place-items-center">
                  {line.image ? (
                    <img
                      src={line.image}
                      alt={line.name}
                      loading="lazy"
                      decoding="async"
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    // R17 Q1 — graceful no-image thumbnail (category-aware icon, never the raw SKU).
                    // The row prints the full name beside this thumb, so the fill stays wordless.
                    <NoImageFill icon={iconForCategory(line.category)} variant="thumb" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  {/* R17 Q1 — show a real piece name; a SKU-shaped fallback never surfaces as a
                      "random number" (the underlying line.sku still drives the order payload). */}
                  <p className="truncate font-display text-sm text-ink">
                    {displayPieceName(line.name, line.category, line.sku)}
                  </p>
                  <div className="flex items-center gap-2">
                    {line.category && (
                      <p className="truncate text-[11px] uppercase tracking-wide text-subtle">
                        {line.category}
                      </p>
                    )}
                    {/* R11 #3: on-stand marker in the order-review rows too (inline, no background). */}
                    {line.onStand && <OnStandBadge variant="inline" />}
                  </div>
                </div>
                {/* Quantity stepper */}
                <div className="flex items-center gap-1.5 shrink-0">
                  <StepperButton
                    label={`Decrease quantity of ${line.name}`}
                    icon="dot"
                    onClick={() => applyChange(stepLineQty(lines, line.sku, -1))}
                    disabled={line.qty <= 1}
                  >
                    <span aria-hidden className="text-lg leading-none">
                      &minus;
                    </span>
                  </StepperButton>
                  <span
                    data-testid={`order-qty-${line.sku}`}
                    className="w-8 text-center text-sm tabular-nums text-ink"
                  >
                    {line.qty}
                  </span>
                  <StepperButton
                    label={`Increase quantity of ${line.name}`}
                    icon="plus"
                    onClick={() => applyChange(stepLineQty(lines, line.sku, 1))}
                  >
                    <Icon name="plus" size={14} />
                  </StepperButton>
                </div>
                {/* Remove */}
                <button
                  type="button"
                  aria-label={`Remove ${line.name}`}
                  data-testid={`order-remove-${line.sku}`}
                  onClick={() => applyChange(removeLine(lines, line.sku))}
                  className="shrink-0 rounded-full p-1.5 text-faint hover:text-terracotta hover:bg-fill transition-colors"
                >
                  <Icon name="x" size={15} />
                </button>
              </li>
            ))}
          </ul>

          {/* R28 (7.4) — CTA hierarchy is FELT: Confirm is the gold hero (foil, lifts on hover);
              Back is a quiet ghost-soft peer (controls-vs-commit). R28 (7.5) — the enquiry
              disclaimer is a quiet caption attached directly under the pair (it floated before). */}
          <div className="flex flex-col gap-2 pt-1">
            <div className="flex items-center gap-3">
              <button
                type="button"
                data-testid="order-confirm-btn"
                onClick={handleConfirm}
                className="btn-accent inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-base font-display shadow-elev-lift transition-transform duration-2 ease-physical hover:scale-[1.01] active:scale-[0.99]"
              >
                <Icon name="check" size={16} />
                Confirm &amp; send my spec sheet
              </button>
              <button
                type="button"
                onClick={onBack}
                className="btn-ghost rounded-full px-5 py-3 text-sm text-subtle hover:text-ink"
              >
                Back
              </button>
            </div>
            <p className="text-[11px] leading-relaxed text-faint">
              An enquiry, not a purchase — the team confirms availability, minimums and terms in
              your follow-up.
            </p>
          </div>
        </>
      )}
    </div>
  );
}

function StepperButton({
  label,
  onClick,
  disabled,
  children,
}: {
  label: string;
  icon: 'plus' | 'dot';
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  // R28 (7.1) — neumorphic stepper: a lifted disc (`.soft-lift`, Wave-1 elev-lift) that SINKS to
  // elev-inset on press — the tactile "push". Disabled → no lift (flat, so it reads inert).
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      disabled={disabled}
      className={`grid h-9 w-9 place-items-center rounded-full bg-surface text-ink transition-colors hover:text-brass disabled:text-faint disabled:opacity-40 ${
        disabled ? 'ring-1 ring-line' : 'soft-lift'
      }`}
    >
      {children}
    </button>
  );
}
